import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { ApiError } from '@/api/client';
import { listLocations } from '@/api/locations';
import {
  emptyRules,
  getEffectiveSchedulingRules,
  getTeamAllowedLocationsOverride,
  getTeamRuleOverride,
  setTeamAllowedLocationsOverride,
  setTeamRuleOverride,
  type AllowedLocationEntry,
  type Rules,
} from '@/api/teamCategories';
import type { Team } from '@/api/teams';
import type { TeamPanelHandle } from '@/components/teamPanelHandle';
import { RulesForm } from '@/components/RulesForm';

// TeamRulesPanel is the editable schedule-rules surface for one team,
// without the dialog chrome — meant to be embedded inside the team sheet's
// "Schedule rules" tab. Owns its own data lifecycle: loads the effective
// rules + override + locations on mount, seeds the form, PATCHes back on
// save.
//
// selectPortalContainer: where the Radix Select dropdowns inside the
// rules form portal into. Pass the sheet content node so dropdowns layer
// above the sheet instead of behind it.
export interface TeamRulesPanelProps {
  slug: string;
  team: Team;
  selectPortalContainer?: HTMLElement | null | undefined;
}

export const TeamRulesPanel = forwardRef<TeamPanelHandle, TeamRulesPanelProps>(
  function TeamRulesPanel({ slug, team, selectPortalContainer }, ref) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [override, setOverride] = useState<Rules>(emptyRules);
  const [selectedLocationIDs, setSelectedLocationIDs] = useState<Set<string>>(
    new Set(),
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const effectiveQuery = useQuery({
    queryKey: ['team-effective-rules', slug, team.id],
    queryFn: () => getEffectiveSchedulingRules(token, slug, team.id),
    enabled: !!token && !!slug,
  });
  const overrideQuery = useQuery({
    queryKey: ['team-rule-override', slug, team.id],
    queryFn: () => getTeamRuleOverride(token, slug, team.id),
    enabled: !!token && !!slug,
  });
  const overrideLocsQuery = useQuery({
    queryKey: ['team-allowed-locations-override', slug, team.id],
    queryFn: () => getTeamAllowedLocationsOverride(token, slug, team.id),
    enabled: !!token && !!slug,
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', slug],
    queryFn: () => listLocations(token, slug),
    enabled: !!token && !!slug,
  });

  // Seed once per team — a ref-guarded flag prevents subsequent refetches
  // from clobbering in-progress edits.
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededRef.current === team.id) return;
    if (!overrideQuery.data) return;
    if (!overrideLocsQuery.data) return;
    setOverride(overrideQuery.data);
    setSelectedLocationIDs(
      new Set(overrideLocsQuery.data.entries.map((e) => e.location_id)),
    );
    seededRef.current = team.id;
  }, [team.id, overrideQuery.data, overrideLocsQuery.data]);

  const isLoading =
    effectiveQuery.isPending ||
    overrideQuery.isPending ||
    overrideLocsQuery.isPending ||
    locationsQuery.isPending;

  // Imperative commit — driven by TeamSheet's footer Save button. No
  // local validation; the rules form's dropdowns are constrained by
  // construction. Returns true on success, throws on server failure.
  useImperativeHandle(
    ref,
    () => ({
      commit: async () => {
        if (isLoading) return true; // nothing to save yet
        setErrorCode(null);
        setSaving(true);
        try {
          await setTeamRuleOverride(token, slug, team.id, override);
          const entries: AllowedLocationEntry[] = Array.from(
            selectedLocationIDs,
          ).map((id, i) => ({
            location_id: id,
            zone_id: null,
            preference_order: i,
          }));
          await setTeamAllowedLocationsOverride(token, slug, team.id, entries);
          void queryClient.invalidateQueries({
            queryKey: ['team-rule-override', slug, team.id],
          });
          void queryClient.invalidateQueries({
            queryKey: ['team-allowed-locations-override', slug, team.id],
          });
          void queryClient.invalidateQueries({
            queryKey: ['team-effective-rules', slug, team.id],
          });
          return true;
        } catch (err) {
          setErrorCode(err instanceof ApiError ? err.code : 'unknown');
          throw err;
        } finally {
          setSaving(false);
        }
      },
    }),
    [isLoading, override, selectedLocationIDs, queryClient, slug, team.id, token],
  );

  const inherited = effectiveQuery.data?.rules;
  const categoryName = effectiveQuery.data?.category_name ?? '';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="font-heading text-base font-semibold text-foreground">
          {t('teams.rules.panelHeading')}
        </h3>
        {categoryName ? (
          <p className="mt-1 text-sm text-muted-foreground">
            {t('teams.rules.descriptionCategorised', { category: categoryName })}
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            {t('teams.rules.descriptionUncategorised')}
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('teams.rules.loading')}
        </p>
      ) : (
        <RulesForm
          rules={override}
          onChange={setOverride}
          inherited={inherited}
          inheritedLabel={categoryName || t('teams.rules.inheritedLabelFallback')}
          locations={locationsQuery.data?.locations ?? []}
          selectedLocationIDs={selectedLocationIDs}
          onSelectedLocationsChange={setSelectedLocationIDs}
          {...(selectPortalContainer
            ? { selectPortalContainer }
            : {})}
          disabled={saving}
        />
      )}

      {errorCode && (
        <p role="alert" className="text-sm text-destructive">
          {t(`teams.rules.error.${errorCode}`, {
            defaultValue: t('teams.rules.error.unknown'),
          })}
        </p>
      )}
    </div>
  );
});
